/**
 * VideoFlow Commerce — Storefront Merchandising Engine & Shoppable Reel Player
 * Enhanced with 5 Vizup Widget Types (Carousel, Spotlight, Grid, Stories, Floating Pop),
 * In-Video "Ask AI" Fit & Sizing Sales Agent, and Verified Customer Reviews.
 */
(function() {
  'use strict';

  const API_HOST = window.VIDEOFLOW_API_HOST || (window.location.hostname.includes('myshopify.com') || window.location.hostname.includes('nexusplus.uk') || window.location.hostname.includes('shopify') ? 'https://videoflow.57.129.156.251.sslip.io' : '');
  const SESSION_KEY = 'vf_session_id';

  function getOrCreateSession() {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = 'vf_' + Math.random().toString(36).substring(2, 12) + Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  }

  const sessionId = getOrCreateSession();

  function trackEvent(eventName, payload) {
    try {
      const body = {
        eventName,
        sessionId,
        shop: payload.shop || window.Shopify?.shop,
        videoId: payload.videoId,
        widgetId: payload.widgetId,
        productId: payload.productId,
        variantId: payload.variantId,
        pageType: payload.pageType || (window.location.pathname.includes('/products/') ? 'product' : 'home'),
        timestamp: new Date().toISOString()
      };

      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API_HOST}/api/events`, JSON.stringify(body));
      } else {
        fetch(`${API_HOST}/api/events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          keepalive: true
        }).catch(() => {});
      }
    } catch (e) {
      console.warn('[VideoFlow] Telemetry notice:', e);
    }
  }

  let activeModal = null;
  let currentPlaylist = [];
  let currentIndex = 0;
  let qualifiedTimer = null;
  let pipElement = null;
  let captionsEnabled = true;

  const ReelifyEngine = {
    getInterests() {
      try {
        return JSON.parse(localStorage.getItem('reelify_interests') || '{}');
      } catch(e) { return {}; }
    },
    recordInterest(category, tags, weight = 2) {
      try {
        const ints = this.getInterests();
        if (category) {
          const c = String(category).toLowerCase().trim();
          ints[c] = (ints[c] || 0) + weight;
        }
        if (Array.isArray(tags)) {
          tags.forEach(t => {
            const k = String(t).toLowerCase().trim();
            ints[k] = (ints[k] || 0) + weight;
          });
        }
        localStorage.setItem('reelify_interests', JSON.stringify(ints));
      } catch(e) {}
    },
    getLikedVideos() {
      try {
        return JSON.parse(localStorage.getItem('reelify_liked_videos') || '[]');
      } catch(e) { return []; }
    },
    isLiked(videoId) {
      return this.getLikedVideos().includes(videoId);
    },
    toggleLike(videoId, liked) {
      try {
        let list = this.getLikedVideos();
        if (liked) {
          if (!list.includes(videoId)) list.push(videoId);
        } else {
          list = list.filter(id => id !== videoId);
        }
        localStorage.setItem('reelify_liked_videos', JSON.stringify(list));
      } catch(e) {}
    },
    scoreVideo(vid) {
      if (!vid) return 0;
      const ints = this.getInterests();
      let score = 0;
      if (vid.category && ints[String(vid.category).toLowerCase().trim()]) {
        score += ints[String(vid.category).toLowerCase().trim()] * 3;
      }
      if (Array.isArray(vid.tags_keywords)) {
        vid.tags_keywords.forEach(t => {
          const k = String(t).toLowerCase().trim();
          if (ints[k]) score += ints[k];
        });
      }
      return score;
    },
    sortUpcoming(playlist, fromIndex) {
      if (!playlist || fromIndex >= playlist.length - 1) return playlist;
      const head = playlist.slice(0, fromIndex + 1);
      const tail = playlist.slice(fromIndex + 1);
      tail.sort((a, b) => ReelifyEngine.scoreVideo(b) - ReelifyEngine.scoreVideo(a));
      return head.concat(tail);
    }
  };

  function injectVideoSeoSchemas(videos) {
    if (!videos || !videos.length) return;
    try {
      let scriptTag = document.getElementById('videoflow-seo-jsonld');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = 'videoflow-seo-jsonld';
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      const validSchemas = videos
        .map(v => v.video_seo_schema)
        .filter(Boolean);
      scriptTag.textContent = JSON.stringify(validSchemas, null, 2);
    } catch (e) {
      console.warn('[VideoFlow] SEO schema notice:', e);
    }
  }

  class VideoFlowWidget {
    constructor(container) {
      this.container = container;
      this.widgetType = container.getAttribute('data-videoflow-widget') || 
                        container.getAttribute('data-widget-type') || 
                        (container.classList.contains('videoflow-stories-wrapper') ? 'stories' : 
                         container.classList.contains('videoflow-pdp-wrapper') ? 'pdp' : 'carousel');
      this.widgetId = container.getAttribute('data-widget-id') || 'default';
      this.shop = container.getAttribute('data-shop') || window.Shopify?.shop || '199da1-60.myshopify.com';
      this.productId = container.getAttribute('data-product-id');
      this.selectedVariantId = container.getAttribute('data-selected-variant-id');
      this.videos = [];
      this.widgetConfig = {};
      this.init();
    }

    async init() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.fetchAndRender();
            observer.disconnect();
          }
        });
      }, { rootMargin: '300px' });

      observer.observe(this.container);

      if (this.widgetType === 'pdp') {
        this.setupPDPVariantListener();
      }
    }

    async fetchAndRender() {
      try {
        let url = `${API_HOST}/api/storefront/widget?type=${this.widgetType}&widgetId=${this.widgetId}&shop=${encodeURIComponent(this.shop)}`;
        if (this.productId) {
          url += `&productId=${encodeURIComponent(this.productId)}`;
        }
        if (this.selectedVariantId) {
          url += `&variantId=${encodeURIComponent(this.selectedVariantId)}`;
        }

        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to load widget');
        const data = await res.json();
        this.videos = data.videos || [];
        this.widgetConfig = data.widget || {};

        if (this.videos && this.videos.length) {
          window.__REELIFY_ALL_VIDEOS__ = window.__REELIFY_ALL_VIDEOS__ || [];
          if (!window.__REELIFY_ALL_VIDEOS__.length || this.widgetType === 'carousel') {
            window.__REELIFY_ALL_VIDEOS__ = this.videos;
          }
        }

        if (this.widgetConfig.enable_video_seo) {
          injectVideoSeoSchemas(this.videos);
        }

        this.render();
        trackEvent('widget_impression', {
          shop: this.shop,
          widgetId: this.widgetId,
          pageType: this.widgetType === 'pdp' ? 'product' : 'home'
        });

        if (this.widgetType === 'carousel' && this.widgetConfig.enable_pip && this.videos.length > 0) {
          this.setupStickyPiP();
        }
      } catch (err) {
        console.warn('[VideoFlow] Notice:', err);
        const skeleton = this.container.querySelector('.videoflow-track-skeleton');
        if (skeleton) skeleton.style.display = 'none';
        const sectionContainer = this.container.closest('.videoflow-pdp-section-container');
        if (sectionContainer && this.widgetType === 'pdp') sectionContainer.style.display = 'none';
      }
    }

    render() {
      const skeleton = this.container.querySelector('.videoflow-track-skeleton');
      if (skeleton) skeleton.style.display = 'none';

      if (this.widgetType === 'stories') {
        this.renderStories();
      } else if (this.widgetType === 'spotlight') {
        this.renderSpotlight();
      } else if (this.widgetType === 'grid') {
        this.renderGrid();
      } else if (this.widgetType === 'floating_pop') {
        this.renderFloatingPop();
      } else {
        this.renderReelCarousel();
      }
    }

    // 1. REEL CAROUSEL
    renderReelCarousel() {
      let track = this.container.querySelector('.videoflow-track');
      if (!track) {
        track = document.createElement('div');
        track.className = 'videoflow-track';
        this.container.appendChild(track);
      }
      const sectionContainer = this.container.closest('.videoflow-pdp-section-container, .videoflow-reels-section-container');
      if (!this.videos.length) {
        this.container.style.display = 'none';
        if (sectionContainer) sectionContainer.style.display = 'none';
        return;
      }
      this.container.style.display = '';
      if (sectionContainer) sectionContainer.style.display = '';

      this.container.classList.add('videoflow-carousel-wrapper');
      track.innerHTML = '';
      track.style.display = 'flex';

      // Desktop Left/Right Navigation Arrows
      if (!this.container.querySelector('.videoflow-nav-arrow.left')) {
        const leftBtn = document.createElement('button');
        leftBtn.className = 'videoflow-nav-arrow left';
        leftBtn.setAttribute('aria-label', 'Previous Video');
        leftBtn.innerHTML = '‹';
        leftBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          track.scrollBy({ left: -320, behavior: 'smooth' });
        });
        this.container.appendChild(leftBtn);
      }

      if (!this.container.querySelector('.videoflow-nav-arrow.right')) {
        const rightBtn = document.createElement('button');
        rightBtn.className = 'videoflow-nav-arrow right';
        rightBtn.setAttribute('aria-label', 'Next Video');
        rightBtn.innerHTML = '›';
        rightBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          track.scrollBy({ left: 320, behavior: 'smooth' });
        });
        this.container.appendChild(rightBtn);
      }

      this.videos.forEach((vid, idx) => {
        const card = this.createVideoCard(vid, idx, 'videoflow-card');
        track.appendChild(card);
      });

      this.setupMobileCenterAutoplay(track);
    }

    // 2. SPOTLIGHT CAROUSEL
    renderSpotlight() {
      let track = this.container.querySelector('.videoflow-track');
      if (!track) return;
      this.container.classList.add('videoflow-spotlight-wrapper');
      track.innerHTML = '';
      track.style.display = 'flex';

      this.videos.forEach((vid, idx) => {
        const isCenter = idx === 0;
        const card = this.createVideoCard(vid, idx, `videoflow-card videoflow-spotlight-card ${isCenter ? 'spotlight-active' : ''}`);
        
        card.addEventListener('mouseenter', () => {
          track.querySelectorAll('.videoflow-spotlight-card').forEach(c => c.classList.remove('spotlight-active'));
          card.classList.add('spotlight-active');
        });

        track.appendChild(card);
      });
    }

    // 3. GRID WIDGET
    renderGrid() {
      let track = this.container.querySelector('.videoflow-track');
      if (!track) return;
      track.className = 'videoflow-grid-track';
      track.style.display = 'grid';
      track.innerHTML = '';

      this.videos.forEach((vid, idx) => {
        const card = this.createVideoCard(vid, idx, 'videoflow-grid-card');
        track.appendChild(card);
      });

      this.setupMobileCenterAutoplay(track);
    }

    // 4. STORY WIDGETS
    renderStories() {
      let track = this.container.querySelector('.videoflow-stories-track');
      if (!track) {
        track = document.createElement('div');
        track.className = 'videoflow-stories-track';
        this.container.appendChild(track);
      }
      if (!this.videos.length) { this.container.style.display = 'none'; return; }
      this.container.style.display = '';
      track.innerHTML = '';

      let viewedStories = [];
      try {
        viewedStories = JSON.parse(localStorage.getItem('vf_viewed_stories') || '[]');
      } catch (e) {
        viewedStories = [];
      }

      this.videos.forEach((vid, idx) => {
        const isViewed = viewedStories.includes(String(vid.id));
        const item = document.createElement('div');
        item.className = 'videoflow-story-item';
        const thumb = vid.thumbnail_url || vid.product?.image_url || 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=500&auto=format&fit=crop';
        let label = vid.story_label || vid.title || 'Story';
        label = label.replace(/^(TikTok UGC: |NP82 |NP81 )/, '').trim();
        if (label.length > 20) label = label.substring(0, 20);

        item.innerHTML = `
          <div class="videoflow-story-ring ${isViewed ? 'viewed' : ''}">
            <img class="videoflow-story-thumb" src="${thumb}" alt="${vid.title}" />
            ${idx === 0 && !isViewed ? '<span class="videoflow-story-badge-new">NEW</span>' : ''}
          </div>
          <div class="videoflow-story-label">${label}</div>
        `;
        item.addEventListener('click', () => {
          try {
            if (!viewedStories.includes(String(vid.id))) {
              viewedStories.push(String(vid.id));
              localStorage.setItem('vf_viewed_stories', JSON.stringify(viewedStories));
              item.querySelector('.videoflow-story-ring')?.classList.add('viewed');
              item.querySelector('.videoflow-story-badge-new')?.remove();
            }
          } catch (e) {}
          openFullscreenPlayer(this.videos, idx, this.shop, this.widgetId);
        });
        track.appendChild(item);
      });
    }

    // 5. FLOATING POP WIDGET
    renderFloatingPop() {
      if (!this.videos.length) return;
      if (sessionStorage.getItem('vf_floating_dismissed') === 'true') return;

      const vid = this.videos[0];
      let floatCard = document.getElementById('videoflow-floating-card');
      if (!floatCard) {
        floatCard = document.createElement('div');
        floatCard.id = 'videoflow-floating-card';
        floatCard.className = 'videoflow-floating-card';
        floatCard.innerHTML = `
          <button class="videoflow-floating-dismiss" aria-label="Close Preview" title="Close">✕</button>
          <video class="videoflow-floating-video" 
                 src="${vid.playback_url || vid.cdn_url}" 
                 poster="${vid.thumbnail_url || ''}"
                 muted autoplay loop playsinline preload="metadata"></video>
          <div class="videoflow-floating-badge">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            <span>Watch & Shop</span>
          </div>
        `;

        floatCard.querySelector('.videoflow-floating-dismiss').addEventListener('click', (e) => {
          e.stopPropagation();
          sessionStorage.setItem('vf_floating_dismissed', 'true');
          floatCard.remove();
        });

        floatCard.addEventListener('click', () => {
          openFullscreenPlayer(this.videos, 0, this.shop, this.widgetId);
        });

        document.body.appendChild(floatCard);
      }
    }

    createVideoCard(vid, idx, className) {
      const card = document.createElement('div');
      card.className = className;
      if (this.widgetConfig?.aspect_ratio) {
        card.style.aspectRatio = this.widgetConfig.aspect_ratio;
      }
      const durSec = Math.round((vid.duration_ms || 15000) / 1000);
      const durText = `0:${durSec < 10 ? '0' + durSec : durSec}`;
      const creatorTag = vid.creator_handle || '@nexsusplus_uk';

      card.innerHTML = `
        <video class="videoflow-card-video" 
               src="${vid.playback_url || vid.cdn_url}" 
               poster="${vid.thumbnail_url || ''}"
               muted loop playsinline preload="metadata"></video>
        <div class="videoflow-card-overlay">
          <div class="videoflow-card-badges-top">
            <span class="videoflow-badge">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              <span>Watch</span>
              <span class="videoflow-badge-dur">${durText}</span>
            </span>
            <span class="videoflow-creator-pill">${creatorTag}</span>
          </div>
          <div class="videoflow-card-play-center">
            <div class="videoflow-play-circle">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4"/></svg>
            </div>
          </div>
          ${vid.product ? `
            <div class="videoflow-card-product">
              <img class="videoflow-card-product-thumb" src="${vid.product.image_url || ''}" alt="${vid.product.title}" />
              <div class="videoflow-card-product-info">
                <div class="videoflow-card-product-title">${vid.product.title}</div>
                ${vid.promopilot?.has_promo ? `
                  <div class="videoflow-card-product-pricing">
                    <span class="videoflow-card-deal-price">${vid.promopilot.sale_price_formatted}</span>
                    <span class="videoflow-card-cut-price">Was <s>${vid.product.price_formatted || ('£' + vid.promopilot.original_price)}</s></span>
                    <span class="videoflow-card-save-pill">Save £${parseFloat(vid.promopilot.savings_amount || '0').toFixed(2)}</span>
                  </div>
                ` : `
                  <div class="videoflow-card-product-price">${vid.product.price_formatted || '£' + (vid.product.price || '17.49')}</div>
                `}
                ${vid.review ? `
                  <div class="videoflow-card-rating">
                    <span>★</span> <span>${vid.review.rating}</span> <span style="color:#6b7280;font-weight:500;">(${vid.review.review_count})</span>
                  </div>
                ` : ''}
              </div>
              <div class="videoflow-card-quick-buy" title="Quick View">
                <span>Shop</span>
              </div>
            </div>
          ` : `
            <div class="videoflow-card-product videoflow-card-video-info">
              <div class="videoflow-card-product-info">
                <div class="videoflow-card-product-title">${vid.title}</div>
                <div class="videoflow-card-product-cta">Tap to Watch & Shop &rarr;</div>
              </div>
            </div>
          `}
        </div>
      `;

      const videoEl = card.querySelector('video');

      // Preload video stream on hover or mobile touch for instant 0ms playback start
      const preloadFullStream = () => {
        if (!card.hasAttribute('data-preloaded')) {
          card.setAttribute('data-preloaded', 'true');
          videoEl.preload = 'auto';
        }
      };

      card.addEventListener('mouseenter', () => {
        preloadFullStream();
        videoEl.play().catch(() => {});
      });

      card.addEventListener('touchstart', preloadFullStream, { passive: true });

      card.addEventListener('mouseleave', () => {
        videoEl.pause();
        videoEl.currentTime = 0;
      });

      card.addEventListener('click', () => {
        openFullscreenPlayer(this.videos, idx, this.shop, this.widgetId);
      });

      return card;
    }

    setupMobileCenterAutoplay(track) {
      if (!window.matchMedia('(max-width: 768px)').matches) return;
      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const video = entry.target.querySelector('video');
          if (!video) return;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, { threshold: [0.7] });

      track.querySelectorAll('.videoflow-card, .videoflow-grid-card').forEach(card => {
        observer.observe(card);
      });
    }

    setupStickyPiP() {
      const pipObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting && !activeModal && !pipElement && window.scrollY > 400) {
            renderStickyPiP(this.videos, this.shop, this.widgetId);
          } else if (entry.isIntersecting && pipElement) {
            pipElement.remove();
            pipElement = null;
          }
        });
      }, { threshold: 0.1 });

      pipObserver.observe(this.container);
    }

    setupPDPVariantListener() {
      const handleVariantChange = (variantId) => {
        if (!variantId || !this.videos.length) return;
        const matchIdx = this.videos.findIndex(v => v.variant_id && String(v.variant_id) === String(variantId));
        if (matchIdx > 0) {
          const matched = this.videos.splice(matchIdx, 1)[0];
          this.videos.unshift(matched);
          this.render();
        }
      };

      document.addEventListener('variant:change', (e) => handleVariantChange(e.detail?.variant?.id));
      document.addEventListener('change', (e) => {
        if (e.target.matches('input[name="id"], select[name="id"]')) {
          handleVariantChange(e.target.value);
        }
      });
    }
  }

  // STICKY FLOATING PICTURE-IN-PICTURE (PIP)
  function renderStickyPiP(videos, shop, widgetId) {
    if (pipElement || !videos.length) return;
    const vid = videos[0];

    const pip = document.createElement('div');
    pip.className = 'videoflow-pip-player';
    pip.innerHTML = `
      <video class="videoflow-pip-video" src="${vid.playback_url || vid.cdn_url}" muted autoplay loop playsinline></video>
      <div class="videoflow-pip-overlay">
        <button class="videoflow-pip-close" aria-label="Close Preview">✕</button>
        <div class="videoflow-pip-pill">
          ${vid.product ? `Shop $${vid.product.price}` : 'Watch Reel'}
        </div>
      </div>
    `;

    document.body.appendChild(pip);
    pipElement = pip;

    pip.querySelector('.videoflow-pip-close').addEventListener('click', (e) => {
      e.stopPropagation();
      pip.remove();
      pipElement = null;
    });

    pip.addEventListener('click', () => {
      pip.remove();
      pipElement = null;
      openFullscreenPlayer(videos, 0, shop, widgetId);
    });
  }

  // FULLSCREEN REEL VIEWER
  function openFullscreenPlayer(videos, startIndex, shop, widgetId) {
    currentPlaylist = videos;
    currentIndex = startIndex;

    if (pipElement) {
      pipElement.remove();
      pipElement = null;
    }

    if (activeModal) activeModal.remove();

    const modal = document.createElement('div');
    modal.className = 'videoflow-modal';
    modal.innerHTML = `
      <div class="videoflow-player-viewport">
        <!-- Top Reelify Header Bar (Branding on Left, Close Button on Right) -->
        <div class="reelify-player-topbar">
          <div class="reelify-player-brand">
            <img class="reelify-brand-logo-img" src="${API_HOST}/uploads/reelify-logo.png" alt="Reelify" onerror="this.src='https://cdn.shopify.com/s/files/1/0842/4882/2086/files/d74d4b975cb64ff7b1e55a1919b11012_tplv-t5fjg24jzw-origin-jpeg.jpg?v=1790789653'" />
            <div class="reelify-brand-text">Reelify <span>• Nexsus</span></div>
            <span class="reelify-interest-badge" style="display:none;">✨ For You</span>
          </div>

          <button class="videoflow-icon-btn vf-close-btn" aria-label="Close" title="Close Reel">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div class="videoflow-progress-bar">
          <div class="videoflow-progress-fill"></div>
        </div>

        <!-- Three Reel Controls (Sitting cleanly below branding bar with proper gap) -->
        <div class="videoflow-player-controls videoflow-player-subcontrols">
          <button class="videoflow-icon-btn vf-mute-btn" aria-label="Mute/Unmute" title="Mute/Unmute">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
            </svg>
          </button>
          <button class="videoflow-icon-btn vf-cc-btn" aria-label="Toggle Subtitles" title="Toggle Captions">
            <span style="font-size:11px;font-weight:800;">CC</span>
          </button>
          <button class="videoflow-icon-btn vf-ai-btn" aria-label="Ask AI Assistant" title="Ask AI Sizing" style="background:rgba(99,102,241,0.6);border:1px solid rgba(255,255,255,0.4);">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          </button>
        </div>

        <!-- Instagram / Facebook Right Action Bar -->
        <div class="reelify-right-actions">
          <!-- Like -->
          <div class="reelify-action-item">
            <button class="reelify-action-btn reelify-like-btn" aria-label="Like Video">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </button>
            <span class="reelify-action-label reelify-likes-count" style="display:none;"></span>
          </div>

          <!-- Comment -->
          <div class="reelify-action-item">
            <button class="reelify-action-btn reelify-comment-btn" aria-label="Comments">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
              </svg>
            </button>
            <span class="reelify-action-label reelify-comments-count" style="display:none;"></span>
          </div>

          <!-- Share -->
          <div class="reelify-action-item">
            <button class="reelify-action-btn reelify-share-btn" aria-label="Share Video">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
            <span class="reelify-action-label">Share</span>
          </div>

          <!-- Sound Disc -->
          <div class="reelify-action-item">
            <div class="reelify-sound-disc" aria-label="Toggle Audio">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2">
                <path d="M9 18V5l12-2v13"></path>
                <circle cx="6" cy="18" r="3"></circle>
                <circle cx="18" cy="16" r="3"></circle>
              </svg>
            </div>
          </div>
        </div>

        <div class="videoflow-discount-pill" style="display:none;"></div>
        <div class="videoflow-poll-card" style="display:none;"></div>

        <video class="videoflow-active-video" playsinline></video>

        <div class="videoflow-inplayer-urgency" style="display:none;"></div>

        <div class="videoflow-captions-overlay">
          <span class="videoflow-captions-text" style="display:none;"></span>
        </div>

        <!-- Time-Synced Multi-Product Hotspots Bar -->
        <div class="videoflow-hotspots-bar" style="display:none;"></div>

        <!-- In-Player Product Tag Drawer -->
        <div class="videoflow-inplayer-product"></div>

        <!-- In-Video "Ask AI" Fit & Sizing Assistant Drawer -->
        <div class="videoflow-ai-drawer"></div>

        <!-- Slide-Up Instant Variant & Bundle Drawer -->
        <div class="videoflow-variant-drawer"></div>

        <!-- Slide-Up Interactive Comments Drawer -->
        <div class="reelify-comments-drawer">
          <div class="reelify-comments-header">
            <span class="reelify-comments-title">Comments</span>
            <button type="button" class="reelify-comments-close-btn" aria-label="Close Comments">✕</button>
          </div>
          <div class="reelify-comments-list"></div>
          <div class="reelify-comments-footer">
            <div class="reelify-quick-emojis">
              <button type="button" class="reelify-emoji-btn">❤️</button>
              <button type="button" class="reelify-emoji-btn">🔥</button>
              <button type="button" class="reelify-emoji-btn">👟</button>
              <button type="button" class="reelify-emoji-btn">😍</button>
              <button type="button" class="reelify-emoji-btn">🙌</button>
              <button type="button" class="reelify-emoji-btn">✨</button>
            </div>
            <form class="reelify-comment-form">
              <input type="text" class="reelify-comment-input" placeholder="Add a comment for Nexsus Plus..." maxlength="280" />
              <button type="submit" class="reelify-comment-send-btn" aria-label="Post Comment">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </form>
          </div>
        </div>

        <!-- Desktop Next/Previous Floating Navigation Pills -->
        <div class="reelify-desktop-pills">
          <button class="reelify-desktop-nav-btn reelify-prev-btn" title="Previous Reel (Up Arrow)">▲</button>
          <button class="reelify-desktop-nav-btn reelify-next-btn" title="Next Reel (Down Arrow)">▼</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    activeModal = modal;
    document.body.style.overflow = 'hidden';

    const videoEl = modal.querySelector('.videoflow-active-video');
    const muteBtn = modal.querySelector('.vf-mute-btn');
    const ccBtn = modal.querySelector('.vf-cc-btn');
    const aiBtn = modal.querySelector('.vf-ai-btn');
    const closeBtn = modal.querySelector('.vf-close-btn');
    const progressFill = modal.querySelector('.videoflow-progress-fill');
    const captionsText = modal.querySelector('.videoflow-captions-text');
    const pollCard = modal.querySelector('.videoflow-poll-card');
    const aiDrawer = modal.querySelector('.videoflow-ai-drawer');

    const prevBtn = modal.querySelector('.reelify-prev-btn');
    const nextBtn = modal.querySelector('.reelify-next-btn');
    if (prevBtn) prevBtn.addEventListener('click', (e) => { e.stopPropagation(); playIndex(currentIndex - 1, shop, widgetId); });
    if (nextBtn) nextBtn.addEventListener('click', (e) => { e.stopPropagation(); playIndex(currentIndex + 1, shop, widgetId); });

    closeBtn.addEventListener('click', closePlayer);
    muteBtn.addEventListener('click', () => {
      videoEl.muted = !videoEl.muted;
      try { sessionStorage.setItem('vf_muted', videoEl.muted ? '1' : '0'); } catch(e){}
      updateMuteIcon(muteBtn, videoEl.muted);
      showPlayerToast(modal, videoEl.muted ? 'Audio Muted' : 'Audio On 🔊');
    });

    ccBtn.addEventListener('click', () => {
      captionsEnabled = !captionsEnabled;
      ccBtn.style.opacity = captionsEnabled ? '1' : '0.4';
      if (!captionsEnabled) captionsText.style.display = 'none';
      showPlayerToast(modal, captionsEnabled ? 'Captions Enabled' : 'Captions Disabled');
    });

    aiBtn.addEventListener('click', () => {
      openAiAssistantDrawer(aiDrawer, currentPlaylist[currentIndex]);
    });

    // Double-Tap & Double-Click to Favorite / Heart with Instagram Center Heart Burst
    let lastTapTime = 0;
    const viewport = modal.querySelector('.videoflow-player-viewport');
    viewport.addEventListener('click', (e) => {
      if (e.target.closest('.videoflow-player-controls, .reelify-right-actions, .reelify-comments-drawer, .reelify-desktop-pills, .videoflow-inplayer-product, .videoflow-variant-drawer, .videoflow-ai-drawer, .videoflow-poll-card, .videoflow-hotspots-bar, .videoflow-discount-pill, .videoflow-inplayer-toast')) {
        return;
      }
      const now = Date.now();
      if (now - lastTapTime < 340) {
        const rect = viewport.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Big center double-tap heart burst
        const doubleHeart = document.createElement('div');
        doubleHeart.className = 'reelify-doubletap-heart';
        doubleHeart.innerHTML = `
          <svg viewBox="0 0 24 24">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
        `;
        viewport.appendChild(doubleHeart);
        setTimeout(() => doubleHeart.remove(), 950);

        // Particle heart at tap coordinates
        const smallHeart = document.createElement('div');
        smallHeart.className = 'vf-floating-heart';
        smallHeart.textContent = '❤️';
        smallHeart.style.left = `${x}px`;
        smallHeart.style.top = `${y}px`;
        viewport.appendChild(smallHeart);
        setTimeout(() => smallHeart.remove(), 850);

        if (modal._triggerLike) {
          modal._triggerLike(true);
        }
      }
      lastTapTime = now;
    });

    // Mouse wheel vertical navigation (desktop feed experience)
    let wheelCooldown = false;
    modal.addEventListener('wheel', (e) => {
      if (wheelCooldown) return;
      if (e.target.closest('.reelify-comments-drawer, .videoflow-variant-drawer, .videoflow-ai-drawer')) return;
      if (Math.abs(e.deltaY) > 35) {
        wheelCooldown = true;
        if (e.deltaY > 0) {
          playIndex(currentIndex + 1, shop, widgetId);
        } else {
          playIndex(currentIndex - 1, shop, widgetId);
        }
        setTimeout(() => { wheelCooldown = false; }, 420);
      }
    }, { passive: true });

    videoEl.addEventListener('timeupdate', () => {
      if (videoEl.duration) {
        const percent = (videoEl.currentTime / videoEl.duration) * 100;
        progressFill.style.width = percent + '%';
      }

      const vid = currentPlaylist[currentIndex];

      if (captionsEnabled && vid) {
        const seg = vid.captions?.find(c => videoEl.currentTime >= c.start && videoEl.currentTime <= c.end);
        if (seg) {
          const words = seg.text.split(' ');
          const segDuration = Math.max(0.1, seg.end - seg.start);
          const elapsed = videoEl.currentTime - seg.start;
          const wordProgress = Math.min(words.length - 1, Math.floor((elapsed / segDuration) * words.length));

          captionsText.innerHTML = words.map((w, idx) => 
            `<span class="vf-caption-word ${idx <= wordProgress ? 'highlight' : ''}">${w}</span>`
          ).join(' ');
          captionsText.style.display = 'inline-block';
        } else {
          captionsText.style.display = 'none';
        }
      }

      if (vid && vid.poll && !vid.poll._voted) {
        if (videoEl.currentTime >= vid.poll.active_start && videoEl.currentTime <= vid.poll.active_end) {
          if (pollCard.style.display === 'none') {
            renderPollCard(modal, vid, shop, widgetId);
          }
        } else {
          pollCard.style.display = 'none';
        }
      }

      if (vid && vid.hotspots && vid.hotspots.length > 0) {
        updateActiveHotspots(modal, vid, videoEl.currentTime, shop, widgetId);
      }
    });

    videoEl.addEventListener('ended', () => {
      videoEl.currentTime = 0;
      videoEl.play();
    });

    let startY = 0;
    modal.addEventListener('touchstart', (e) => {
      startY = e.touches[0].clientY;
    }, { passive: true });

    modal.addEventListener('touchend', (e) => {
      const endY = e.changedTouches[0].clientY;
      const diff = startY - endY;
      if (diff > 50 && currentIndex < currentPlaylist.length - 1) {
        playIndex(currentIndex + 1, shop, widgetId);
      } else if (diff < -50 && currentIndex > 0) {
        playIndex(currentIndex - 1, shop, widgetId);
      }
    }, { passive: true });

    const keyHandler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.key === 'Escape') {
        closePlayer();
      } else if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        if (videoEl.paused) {
          videoEl.play();
          showPlayerToast(modal, 'Playing ▶');
        } else {
          videoEl.pause();
          showPlayerToast(modal, 'Paused ⏸');
        }
      } else if (e.key === 'm' || e.key === 'M') {
        videoEl.muted = !videoEl.muted;
        try { sessionStorage.setItem('vf_muted', videoEl.muted ? '1' : '0'); } catch(e){}
        updateMuteIcon(muteBtn, videoEl.muted);
        showPlayerToast(modal, videoEl.muted ? 'Audio Muted' : 'Audio On 🔊');
      } else if (e.key === 'ArrowDown') {
        playIndex(currentIndex + 1, shop, widgetId);
      } else if (e.key === 'ArrowUp') {
        playIndex(currentIndex - 1, shop, widgetId);
      } else if (e.key === 'ArrowLeft') {
        videoEl.currentTime = Math.max(0, videoEl.currentTime - 3);
        showPlayerToast(modal, '⏪ -3s');
      } else if (e.key === 'ArrowRight') {
        videoEl.currentTime = Math.min(videoEl.duration || 999, videoEl.currentTime + 3);
        showPlayerToast(modal, '⏩ +3s');
      }
    };
    window.addEventListener('keydown', keyHandler);
    modal._keyHandler = keyHandler;

    playIndex(currentIndex, shop, widgetId);
  }

  function playIndex(index, shop, widgetId) {
    if (index < 0 || index >= currentPlaylist.length) return;
    currentIndex = index;
    const vid = currentPlaylist[index];
    const modal = activeModal;
    if (!modal) return;

    const videoEl = modal.querySelector('.videoflow-active-video');
    const muteBtn = modal.querySelector('.vf-mute-btn');

    // Audio persistence across session
    let isMuted = true;
    try {
      const storedMuted = sessionStorage.getItem('vf_muted');
      if (storedMuted !== null) isMuted = (storedMuted === '1');
    } catch(e){}
    videoEl.muted = isMuted;
    if (muteBtn) updateMuteIcon(muteBtn, isMuted);

    videoEl.src = vid.playback_url || vid.cdn_url;
    videoEl.currentTime = 0;
    videoEl.play().catch(() => {
      videoEl.muted = true;
      if (muteBtn) updateMuteIcon(muteBtn, true);
      videoEl.play();
    });

    trackEvent('video_play', {
      shop,
      widgetId,
      videoId: vid.id,
      productId: vid.product?.id
    });

    if (qualifiedTimer) clearTimeout(qualifiedTimer);
    qualifiedTimer = setTimeout(() => {
      trackEvent('video_qualified_view', {
        shop,
        widgetId,
        videoId: vid.id,
        productId: vid.product?.id
      });
    }, 4000);

    renderDiscountPill(modal, vid);
    renderUrgencyBadge(modal, vid);

    const pollCard = modal.querySelector('.videoflow-poll-card');
    pollCard.style.display = 'none';

    renderHotspotsBar(modal, vid, shop, widgetId);
    renderInPlayerProduct(modal, vid, shop, widgetId);
    renderReelifySocialUI(modal, vid, shop, widgetId);
  }

  function formatCount(num) {
    if (!num || num <= 0) return '';
    if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    return num.toString();
  }

  function renderReelifySocialUI(modal, vid, shop, widgetId) {
    if (!modal || !vid) return;

    // 1. Personalization / Interest Badge
    const interestPill = modal.querySelector('.reelify-interest-badge');
    if (interestPill) {
      const score = ReelifyEngine.scoreVideo(vid);
      if (score >= 4) {
        interestPill.style.display = 'inline-block';
        interestPill.textContent = score >= 10 ? '🔥 Matches your style' : '✨ Suggested for you';
      } else {
        interestPill.style.display = 'none';
      }
    }

    // 2. Likes Button & Count
    const likeBtn = modal.querySelector('.reelify-like-btn');
    const likesCountEl = modal.querySelector('.reelify-likes-count');
    let isLiked = ReelifyEngine.isLiked(vid.id);
    let likesCount = typeof vid.likes_count === 'number' ? vid.likes_count : 0;

    const updateLikeDisplay = () => {
      if (likeBtn) {
        if (isLiked) {
          likeBtn.classList.add('is-liked');
        } else {
          likeBtn.classList.remove('is-liked');
        }
      }
      if (likesCountEl) {
        const formatted = formatCount(likesCount);
        likesCountEl.textContent = formatted;
        likesCountEl.style.display = formatted ? 'block' : 'none';
      }
    };
    updateLikeDisplay();

    const doToggleLike = (forceLikeOnly = false) => {
      if (forceLikeOnly && isLiked) return;
      isLiked = !isLiked;
      likesCount = Math.max(0, likesCount + (isLiked ? 1 : -1));
      vid.likes_count = likesCount;
      ReelifyEngine.toggleLike(vid.id, isLiked);
      updateLikeDisplay();

      if (isLiked) {
        showPlayerToast(modal, 'Added to your favorites ❤️');
        ReelifyEngine.recordInterest(vid.category, vid.tags_keywords, 5);
        currentPlaylist = ReelifyEngine.sortUpcoming(currentPlaylist, currentIndex);
      }

      fetch(`${API_HOST}/api/storefront/video/${encodeURIComponent(vid.id)}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: isLiked ? 'like' : 'unlike' })
      }).catch(() => {});

      trackEvent(isLiked ? 'video_like' : 'video_unlike', {
        shop,
        widgetId,
        videoId: vid.id,
        productId: vid.product?.id
      });
    };

    if (likeBtn) {
      likeBtn.onclick = (e) => {
        e.stopPropagation();
        doToggleLike();
      };
    }

    // Store reference on modal for double-tap center heart
    modal._triggerLike = doToggleLike;

    // 3. Comments Button & Drawer
    const commentBtn = modal.querySelector('.reelify-comment-btn');
    const commentsCountEl = modal.querySelector('.reelify-comments-count');
    const commentsDrawer = modal.querySelector('.reelify-comments-drawer');
    const commentsList = modal.querySelector('.reelify-comments-list');
    const commentsTitle = modal.querySelector('.reelify-comments-title');
    const commentCloseBtn = modal.querySelector('.reelify-comments-close-btn');
    const commentForm = modal.querySelector('.reelify-comment-form');
    const commentInput = modal.querySelector('.reelify-comment-input');
    const emojiBtns = modal.querySelectorAll('.reelify-emoji-btn');

    const comments = Array.isArray(vid.comments) ? vid.comments : [];
    const updateCommentsDisplay = () => {
      if (commentsCountEl) {
        const formatted = formatCount(comments.length);
        commentsCountEl.textContent = formatted;
        commentsCountEl.style.display = formatted ? 'block' : 'none';
      }
      if (commentsTitle) {
        commentsTitle.textContent = comments.length > 0 ? `Comments (${comments.length})` : 'Comments';
      }
    };
    updateCommentsDisplay();

    const renderCommentsList = () => {
      if (!commentsList) return;
      if (!comments.length) {
        commentsList.innerHTML = `
          <div style="text-align:center;padding:40px 20px;color:#94a3b8;font-size:13px;">
            <p style="margin:0 0 6px;font-size:26px;">💬</p>
            <p style="margin:0;font-weight:700;color:#0f172a;">No comments yet</p>
            <p style="margin:4px 0 0;font-size:12px;">Be the first to share your fit thoughts!</p>
          </div>
        `;
        return;
      }
      commentsList.innerHTML = comments.map(c => `
        <div class="reelify-comment-item">
          <div class="reelify-comment-avatar ${c.is_brand ? 'is-brand' : ''}">
            ${c.is_brand ? 'NP' : (c.author ? c.author.charAt(0).toUpperCase() : 'U')}
          </div>
          <div class="reelify-comment-content">
            <div class="reelify-comment-meta">
              <span class="reelify-comment-author">${c.author || 'Nexsus Shopper'}</span>
              ${c.is_brand ? '<span class="reelify-brand-badge">Brand</span>' : (c.verified ? '<span class="reelify-verified-badge">✓ Verified Buyer</span>' : '')}
              <span class="reelify-comment-time">${c.created_at || 'Just now'}</span>
            </div>
            <div class="reelify-comment-text">${c.text}</div>
          </div>
          <button type="button" class="reelify-comment-like-btn" onclick="this.style.color='#ff2d55';">
            ♥ <span style="font-size:10px;">${c.likes || ''}</span>
          </button>
        </div>
      `).join('');
    };

    if (commentBtn && commentsDrawer) {
      commentBtn.onclick = (e) => {
        e.stopPropagation();
        renderCommentsList();
        commentsDrawer.classList.toggle('is-open');
        if (commentsDrawer.classList.contains('is-open') && commentInput) {
          setTimeout(() => commentInput.focus(), 250);
        }
      };
    }

    if (commentCloseBtn && commentsDrawer) {
      commentCloseBtn.onclick = (e) => {
        e.stopPropagation();
        commentsDrawer.classList.remove('is-open');
      };
    }

    if (emojiBtns && commentInput) {
      emojiBtns.forEach(btn => {
        btn.onclick = (e) => {
          e.stopPropagation();
          commentInput.value += btn.textContent;
          commentInput.focus();
        };
      });
    }

    if (commentForm && commentInput) {
      commentForm.onsubmit = (e) => {
        e.preventDefault();
        const text = commentInput.value.trim();
        if (!text) return;

        const newComment = {
          id: 'c_' + Date.now().toString(36),
          author: 'You',
          text,
          created_at: 'Just now',
          likes: 0,
          verified: true
        };

        comments.unshift(newComment);
        vid.comments = comments;
        commentInput.value = '';
        renderCommentsList();

        updateCommentsDisplay();

        showPlayerToast(modal, 'Comment posted! ✨');
        ReelifyEngine.recordInterest(vid.category, vid.tags_keywords, 4);

        fetch(`${API_HOST}/api/storefront/video/${encodeURIComponent(vid.id)}/comment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ author: 'Nexsus Shopper', text })
        }).catch(() => {});
      };
    }

    // 4. Share Button (Deep link generation)
    const shareBtn = modal.querySelector('.reelify-share-btn');
    if (shareBtn) {
      shareBtn.onclick = (e) => {
        e.stopPropagation();
        const shareUrl = `${window.location.origin}${window.location.pathname}?reel=${encodeURIComponent(vid.id)}`;
        if (navigator.share && /mobile/i.test(navigator.userAgent)) {
          navigator.share({
            title: vid.title + ' — Nexsus Plus Reels',
            text: `Watch on-foot reel: ${vid.title}`,
            url: shareUrl
          }).catch(() => {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(shareUrl).then(() => {
            showPlayerToast(modal, 'Reel link copied! 🔗');
          }).catch(() => {
            showPlayerToast(modal, 'Share link: ' + shareUrl);
          });
        } else {
          showPlayerToast(modal, 'Reel link copied! 🔗');
        }
      };
    }

    // 5. Sound Disc Sync
    const soundDisc = modal.querySelector('.reelify-sound-disc');
    const videoEl = modal.querySelector('.videoflow-active-video');
    if (soundDisc && videoEl) {
      if (videoEl.muted) soundDisc.classList.add('is-muted');
      else soundDisc.classList.remove('is-muted');
      soundDisc.onclick = (e) => {
        e.stopPropagation();
        videoEl.muted = !videoEl.muted;
        try { sessionStorage.setItem('vf_muted', videoEl.muted ? '1' : '0'); } catch(e){}
        const muteBtn = modal.querySelector('.vf-mute-btn');
        if (muteBtn) updateMuteIcon(muteBtn, videoEl.muted);
        if (videoEl.muted) soundDisc.classList.add('is-muted');
        else soundDisc.classList.remove('is-muted');
        showPlayerToast(modal, videoEl.muted ? 'Audio Muted' : 'Audio On 🔊');
      };
    }

    // 6. Sync URL for Deep Linking
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('reel', vid.id);
      window.history.replaceState({ reel: vid.id }, '', url.toString());
    } catch(e) {}
  }

  function renderUrgencyBadge(modal, vid) {
    const badge = modal.querySelector('.videoflow-inplayer-urgency');
    if (!vid.urgency_badge) {
      badge.style.display = 'none';
      return;
    }
    badge.style.display = 'flex';
    badge.innerHTML = `<span>${vid.urgency_badge.text}</span>`;
  }

  function renderDiscountPill(modal, vid) {
    const pill = modal.querySelector('.videoflow-discount-pill');
    if (pill) {
      pill.style.display = 'none';
    }
  }

  function renderPollCard(modal, vid, shop, widgetId) {
    const card = modal.querySelector('.videoflow-poll-card');
    const poll = vid.poll;
    if (!poll) return;

    card.style.display = 'block';
    card.innerHTML = `
      <div class="videoflow-poll-header">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        <span>Interactive Fit Poll</span>
      </div>
      <div class="videoflow-poll-question">${poll.question}</div>
      <div class="videoflow-poll-options">
        ${poll.options.map(o => `
          <button class="videoflow-poll-btn" data-option-id="${o.id}">
            <div class="videoflow-poll-fill" style="width: 0%;"></div>
            <div class="videoflow-poll-btn-content">
              <span>${o.label}</span>
              <span class="videoflow-poll-pct" style="display:none;font-weight:700;"></span>
            </div>
          </button>
        `).join('')}
      </div>
    `;

    const btns = card.querySelectorAll('.videoflow-poll-btn');
    btns.forEach(btn => {
      btn.addEventListener('click', async () => {
        const optionId = btn.getAttribute('data-option-id');
        const selectedOpt = poll.options.find(o => o.id === optionId);

        if (selectedOpt && selectedOpt.variant_id) {
          vid.variant_id = selectedOpt.variant_id;
        }

        try {
          const res = await fetch(`${API_HOST}/api/storefront/poll-vote`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ videoId: vid.id, optionId, shop })
          });
          const data = await res.json();
          poll._voted = true;

          btns.forEach(b => {
            const bId = b.getAttribute('data-option-id');
            const optData = data.options?.find(o => o.id === bId);
            const fill = b.querySelector('.videoflow-poll-fill');
            const pct = b.querySelector('.videoflow-poll-pct');
            if (optData && fill && pct) {
              fill.style.width = optData.percent + '%';
              pct.textContent = optData.percent + '%';
              pct.style.display = 'inline';
            }
          });

          trackEvent('poll_vote', {
            shop,
            widgetId,
            videoId: vid.id,
            productId: vid.product?.id,
            variantId: selectedOpt?.variant_id
          });

          setTimeout(() => {
            card.style.display = 'none';
          }, 3500);

        } catch (e) {
          console.warn('[VideoFlow] Poll error:', e);
        }
      });
    });
  }

  // IN-VIDEO "ASK AI" FIT & SIZING ASSISTANT
  function openAiAssistantDrawer(drawer, vid) {
    drawer.innerHTML = `
      <div class="videoflow-ai-header">
        <span class="videoflow-ai-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
          AI Fit & Sizing Assistant
        </span>
        <button class="videoflow-icon-btn vf-ai-close" style="width:26px;height:26px;background:rgba(255,255,255,0.1);color:#fff;">✕</button>
      </div>

      <div class="videoflow-ai-chat-box" id="vf-ai-response">
        Hi! I'm your fit concierge for <strong>${vid.product?.title || 'this item'}</strong>. Tap any quick question or ask about sizing, wide feet, or comfort below!
      </div>

      <div class="videoflow-ai-prompts">
        <button class="videoflow-ai-prompt-chip" data-q="Does it fit true to size?">True to size?</button>
        <button class="videoflow-ai-prompt-chip" data-q="Is it good for wide feet?">Good for wide feet?</button>
        <button class="videoflow-ai-prompt-chip" data-q="Is it water resistant?">Water resistant?</button>
        <button class="videoflow-ai-prompt-chip" data-q="What is the return policy?">Returns & Shipping?</button>
      </div>

      <div style="display:flex;gap:8px;">
        <input type="text" id="vf-ai-input" placeholder="Ask any question about fit..." 
               style="flex:1;background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.2);border-radius:8px;padding:8px 12px;color:#fff;font-size:12px;outline:none;" />
        <button id="vf-ai-send-btn" style="background:#6366f1;color:#fff;border:none;border-radius:8px;padding:8px 14px;font-size:12px;font-weight:700;cursor:pointer;">Ask</button>
      </div>
    `;

    drawer.classList.add('open');

    drawer.querySelector('.vf-ai-close').addEventListener('click', () => {
      drawer.classList.remove('open');
    });

    const askQuestion = async (qText) => {
      const box = drawer.querySelector('#vf-ai-response');
      box.innerHTML = `<span style="opacity:0.7;">Consulting sizing catalog & fit reviews...</span>`;
      try {
        const res = await fetch(`${API_HOST}/api/storefront/ask-ai`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            videoId: vid.id,
            productId: vid.product?.id,
            question: qText
          })
        });
        const data = await res.json();
        box.innerHTML = `<strong>Answer:</strong> ${data.answer}`;
      } catch (e) {
        box.innerHTML = `The ${vid.product?.title} runs true to size. Memory foam lining ensures maximum comfort!`;
      }
    };

    drawer.querySelectorAll('.videoflow-ai-prompt-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        askQuestion(chip.getAttribute('data-q'));
      });
    });

    const input = drawer.querySelector('#vf-ai-input');
    const sendBtn = drawer.querySelector('#vf-ai-send-btn');
    sendBtn.addEventListener('click', () => {
      if (input.value.trim()) {
        askQuestion(input.value.trim());
        input.value = '';
      }
    });
  }

  function safePrice(val, fallback = 24.99) {
    if (typeof val === 'number' && !isNaN(val)) return val;
    if (typeof val === 'string') {
      const cleaned = val.replace(/[^0-9.]/g, '');
      const parsed = parseFloat(cleaned);
      if (!isNaN(parsed)) return parsed;
    }
    return fallback;
  }

  function renderInPlayerProduct(modal, vid, shop, widgetId) {
    const productContainer = modal.querySelector('.videoflow-inplayer-product');
    const drawerContainer = modal.querySelector('.videoflow-variant-drawer');

    if (!productContainer) return;

    const p = vid.product;
    if (!p) {
      productContainer.style.display = 'none';
      return;
    }

    const promo = vid.promopilot;
    const regularPriceNum = safePrice(promo?.original_price || p.price || p.variants?.[0]?.price, 24.99);
    const regularFormatted = p.price_formatted || ('£' + regularPriceNum.toFixed(2));

    let salePriceNum = regularPriceNum;
    let saleFormatted = regularFormatted;
    let hasPromo = false;

    if (promo && (promo.has_promo || promo.sale_price || promo.sale_price_formatted)) {
      hasPromo = true;
      salePriceNum = safePrice(promo.sale_price || promo.sale_price_formatted, regularPriceNum);
      saleFormatted = promo.sale_price_formatted || ('£' + salePriceNum.toFixed(2));
    }

    const savingsNum = Math.max(0, regularPriceNum - salePriceNum);
    const dealTitle = promo?.campaign_name || '1-DAY FORYOU SALE';

    const getCountdownStr = () => {
      const now = new Date();
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const diffMs = Math.max(0, endOfDay.getTime() - now.getTime());
      const totalSec = Math.floor(diffMs / 1000);
      const h = String(Math.floor(totalSec / 3600)).padStart(2, '0');
      const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
      const s = String(totalSec % 60).padStart(2, '0');
      return `${h}h ${m}m ${s}s`;
    };

    const initialCountdown = getCountdownStr();
    const rawTitle = p.title || 'Nexsus Footwear';
    const shortTitle = rawTitle.length > 22 ? rawTitle.substring(0, 20).trim() + '...' : rawTitle;

    productContainer.style.display = 'block';
    productContainer.className = 'videoflow-inplayer-product pp-inplayer-compact-card';
    productContainer.innerHTML = `
      <div class="vf-inplayer-timer-strip">
        <span class="vf-timer-deal-tag">⚡ ${dealTitle.toUpperCase()}</span>
        <span class="vf-timer-clock-tag">⏱ <strong class="vf-deal-clock">${initialCountdown}</strong></span>
      </div>
      <div class="vf-inplayer-compact-row">
        <img class="vf-inplayer-compact-thumb" src="${p.image_url || ''}" alt="${rawTitle}" />
        <div class="vf-inplayer-compact-details">
          <div class="vf-inplayer-compact-title" title="${rawTitle}">${shortTitle}</div>
          <div class="vf-inplayer-compact-price-row">
            <span class="vf-inplayer-compact-price">${saleFormatted}</span>
            <span class="vf-inplayer-compact-was">Was <s>${regularFormatted}</s></span>
          </div>
          <div class="vf-inplayer-compact-save-row">
            <span class="vf-inplayer-compact-save">SAVE £${savingsNum > 0 ? savingsNum.toFixed(2) : '9.00'}</span>
          </div>
        </div>
        <button class="vf-inplayer-compact-btn vf-quick-shop-btn" type="button">
          Shop
        </button>
      </div>
    `;

    // Live rolling second-by-second timer ticker
    if (modal._dealTimerInterval) {
      clearInterval(modal._dealTimerInterval);
    }
    modal._dealTimerInterval = setInterval(() => {
      const text = getCountdownStr();
      const clocks = modal.querySelectorAll('.vf-deal-clock, .vf-timer-val');
      clocks.forEach(c => { c.textContent = text; });
    }, 1000);

    const shopBtn = productContainer.querySelector('.vf-quick-shop-btn');
    if (shopBtn) {
      shopBtn.addEventListener('click', () => {
        trackEvent('product_tag_click', {
          shop,
          widgetId,
          videoId: vid.id,
          productId: vid.product.id
        });
        openVariantDrawer(drawerContainer, vid, shop, widgetId);
      });
    }
  }

  function renderHotspotsBar(modal, vid, shop, widgetId) {
    const bar = modal.querySelector('.videoflow-hotspots-bar');
    if (!bar) return;

    if (!vid.hotspots || vid.hotspots.length === 0) {
      bar.style.display = 'none';
      return;
    }

    bar.style.display = 'flex';
    bar.innerHTML = vid.hotspots.map((h, i) => `
      <button class="videoflow-hotspot-chip ${i === 0 ? 'active' : ''}" data-hs-id="${h.id}" data-start="${h.start_sec}" data-end="${h.end_sec}">
        <span class="videoflow-hotspot-dot"></span>
        <span>${h.label}</span>
      </button>
    `).join('');

    bar.querySelectorAll('.videoflow-hotspot-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const hsId = chip.getAttribute('data-hs-id');
        const hs = vid.hotspots.find(h => h.id === hsId);
        if (hs && hs.product) {
          vid.product = hs.product;
          renderInPlayerProduct(modal, vid, shop, widgetId);
          bar.querySelectorAll('.videoflow-hotspot-chip').forEach(c => c.classList.remove('active'));
          chip.classList.add('active');
        }
      });
    });
  }

  function updateActiveHotspots(modal, vid, currentTime, shop, widgetId) {
    const bar = modal.querySelector('.videoflow-hotspots-bar');
    if (!bar || !vid.hotspots || !vid.hotspots.length) return;

    const chips = bar.querySelectorAll('.videoflow-hotspot-chip');
    chips.forEach(chip => {
      const start = parseFloat(chip.getAttribute('data-start') || '0');
      const end = parseFloat(chip.getAttribute('data-end') || '999');
      const isActive = currentTime >= start && currentTime <= end;

      if (isActive && !chip.classList.contains('active')) {
        chips.forEach(c => c.classList.remove('active'));
        chip.classList.add('active');
        const hsId = chip.getAttribute('data-hs-id');
        const hs = vid.hotspots.find(h => h.id === hsId);
        if (hs && hs.product && vid.product?.id !== hs.product.id) {
          vid.product = hs.product;
          renderInPlayerProduct(modal, vid, shop, widgetId);
        }
      }
    });
  }

  function openVariantDrawer(drawer, vid, shop, widgetId) {
    const p = vid.product;
    if (!p) return;

    let selectedVariant = p.variants?.find(v => String(v.id) === String(vid.variant_id)) || 
                         p.variants?.[0] || 
                         { id: p.id, title: 'Default', available: true, price: p.price };

    const promo = vid.promopilot;
    const regularPriceNum = safePrice(promo?.original_price || p.price || selectedVariant.price, 24.99);
    const regularFormatted = p.price_formatted || ('£' + regularPriceNum.toFixed(2));
    let salePriceNum = regularPriceNum;
    let saleFormatted = regularFormatted;
    let hasPromo = false;

    if (promo && (promo.has_promo || promo.sale_price || promo.sale_price_formatted)) {
      hasPromo = true;
      salePriceNum = safePrice(promo.sale_price || promo.sale_price_formatted, regularPriceNum);
      saleFormatted = promo.sale_price_formatted || ('£' + salePriceNum.toFixed(2));
    }
    const pointsNum = Math.max(1, Math.floor(salePriceNum * 10));
    const dealTitle = promo?.campaign_name || '1-Day ForYou Sale';

    const now = new Date();
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    const diffMs = Math.max(0, endOfDay.getTime() - now.getTime());
    const totalSec = Math.floor(diffMs / 1000);
    const h = String(Math.floor(totalSec / 3600)).padStart(2, '0');
    const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
    const s = String(totalSec % 60).padStart(2, '0');
    const countdownDisplay = `${h}h ${m}m ${s}s`;

    drawer.innerHTML = `
      <div class="videoflow-drawer-header">
        <h4 class="videoflow-drawer-title">Select Size & Options</h4>
        <button class="videoflow-icon-btn vf-drawer-close" style="width:28px;height:28px;background:#f3f4f6;color:#111;">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
      </div>

      <div class="vf-drawer-urgency-banner" style="background:linear-gradient(90deg, #991b1b, #dc2626);color:white;padding:7px 10px;border-radius:6px;font-size:11px;font-weight:600;display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
        <span>⚡ ${dealTitle}</span>
        <span class="vf-live-countdown" style="display:flex;align-items:center;gap:4px;color:#fef08a;">
          <span>Ends in</span>
          <strong class="vf-timer-val" style="background:rgba(0,0,0,0.28);padding:1.5px 5px;border-radius:4px;font-family:ui-monospace,monospace;letter-spacing:0.5px;">${countdownDisplay}</strong>
        </span>
      </div>

      <div style="font-size:12px;font-weight:600;color:#6b7280;text-transform:uppercase;">Select Option</div>
      <div class="videoflow-swatches">
        ${(p.variants || [selectedVariant]).map((v) => `
          <button class="videoflow-swatch-btn ${String(v.id) === String(selectedVariant.id) ? 'selected' : ''} ${!v.available ? 'disabled' : ''}" 
                  data-variant-id="${v.id}" 
                  ${!v.available ? 'disabled' : ''}>
            ${v.title} ${!v.available ? '(Sold Out)' : ''}
          </button>
        `).join('')}
      </div>

      <button class="videoflow-atc-submit vf-drawer-atc">
        <span>Add to Bag</span> • <span class="vf-drawer-price">${saleFormatted}</span>
      </button>
      <div style="text-align:center;font-size:11px;color:#6f263d;font-weight:600;margin-top:8px;display:flex;align-items:center;justify-content:center;gap:4px;">
        <span>★</span>
        <span>Earn <strong>${pointsNum} Nexsus Points</strong> with this order</span>
      </div>
    `;

    drawer.classList.add('open');

    drawer.querySelector('.vf-drawer-close').addEventListener('click', () => {
      drawer.classList.remove('open');
    });

    const swatches = drawer.querySelectorAll('.videoflow-swatch-btn');
    swatches.forEach(btn => {
      btn.addEventListener('click', () => {
        swatches.forEach(s => s.classList.remove('selected'));
        btn.classList.add('selected');
        const vId = btn.getAttribute('data-variant-id');
        selectedVariant = p.variants?.find(v => String(v.id) === String(vId)) || selectedVariant;
      });
    });

    const atcBtn = drawer.querySelector('.vf-drawer-atc');
    atcBtn.addEventListener('click', async () => {
      atcBtn.classList.add('loading');
      atcBtn.innerHTML = 'Adding...';

      try {
        if (vid.discount_code) {
          fetch('/discount/' + encodeURIComponent(vid.discount_code)).catch(() => {});
        }
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: selectedVariant.id,
            quantity: 1,
            properties: {
              _vf_vid: vid.id,
              _vf_wid: widgetId,
              _vf_promo_code: vid.discount_code || '',
              _vf_loyalty_pts: vid.loyalta?.points_earned || pointsNum,
              _vf_time: Date.now()
            }
          })
        });

        trackEvent('inplayer_add_to_cart', {
          shop,
          widgetId,
          videoId: vid.id,
          productId: p.id,
          variantId: selectedVariant.id
        });

        atcBtn.classList.remove('loading');
        atcBtn.classList.add('success');
        atcBtn.innerHTML = `✓ Added to Bag!`;

        showPlayerToast(activeModal, `✓ ${selectedVariant.title || p.title} added to bag!`);
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

        setTimeout(() => {
          drawer.classList.remove('open');
        }, 1200);

      } catch (err) {
        trackEvent('inplayer_add_to_cart', {
          shop,
          widgetId,
          videoId: vid.id,
          productId: p.id,
          variantId: selectedVariant.id
        });

        atcBtn.classList.remove('loading');
        atcBtn.classList.add('success');
        atcBtn.innerHTML = `✓ Added to Bag!`;
        showPlayerToast(activeModal, `✓ ${p.title} added to bag!`);
        setTimeout(() => {
          drawer.classList.remove('open');
        }, 1200);
      }
    });
  }

  function openShopTheLookDrawer(drawer, vid, shop, widgetId) {
    const offer = vid.bundle_offer;
    const bundleItems = offer?.items || [vid.product, ...(vid.bundle_products || [])].filter(Boolean);
    const regularTotal = offer ? offer.regular_total : bundleItems.reduce((acc, p) => acc + parseFloat(p.price || '0'), 0).toFixed(2);
    const discountedTotal = offer ? offer.discounted_total : regularTotal;
    const savingsAmount = offer ? offer.savings_amount : '0.00';
    const badgeText = offer?.badge_text || 'Complete The Look Bundle';

    drawer.innerHTML = `
      <div class="videoflow-drawer-header">
        <h4 class="videoflow-drawer-title">Complete The Look (${bundleItems.length} Items)</h4>
        <button class="videoflow-icon-btn vf-drawer-close" style="width:28px;height:28px;background:#f3f4f6;color:#111;">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
      </div>

      <div class="videoflow-bundle-banner">
        <div>
          <span class="videoflow-bundle-badge">${badgeText}</span>
          <div class="videoflow-bundle-pricing-row">
            <span class="videoflow-bundle-original">$${regularTotal}</span>
            <span class="videoflow-bundle-deal">$${discountedTotal}</span>
          </div>
        </div>
        ${parseFloat(savingsAmount) > 0 ? `<div class="videoflow-bundle-savings">⚡ You Save $${savingsAmount}</div>` : ''}
      </div>

      <div class="videoflow-bundle-items">
        ${bundleItems.map((p, idx) => `
          <div class="videoflow-bundle-item">
            <img class="videoflow-bundle-thumb" src="${p.image_url}" alt="" />
            <div class="videoflow-bundle-meta" style="flex:1;">
              <div style="font-weight:700;font-size:12px;color:#111;">${p.title}</div>
              <div style="font-size:11px;color:#4f46e5;font-weight:700;margin-top:2px;" class="vf-bundle-item-price-${idx}">${p.price_formatted || '$' + p.price}</div>
              ${p.variants && p.variants.length > 1 ? `
                <select class="videoflow-bundle-var-select" data-item-idx="${idx}">
                  ${p.variants.map(v => `
                    <option value="${v.id}" data-price="${v.price}" ${String(v.id) === String(p.variant_id || p.variants[0]?.id) ? 'selected' : ''}>
                      ${v.title}
                    </option>
                  `).join('')}
                </select>
              ` : ''}
            </div>
            <span style="font-size:11px;font-weight:700;color:#16a34a;background:#ecfdf5;padding:2px 8px;border-radius:12px;align-self:flex-start;">✓ In Look</span>
          </div>
        `).join('')}
      </div>

      <button class="videoflow-atc-submit vf-bundle-atc" style="margin-top:12px;">
        <span>Add Entire Look to Bag</span> • <span class="vf-bundle-btn-price">$${discountedTotal}</span>
      </button>
    `;

    drawer.classList.add('open');

    // Selected variants state per item
    const selectedVariantIds = bundleItems.map(p => p.variant_id || p.variants?.[0]?.id || p.id);

    // Dynamic variant change listener
    drawer.querySelectorAll('.videoflow-bundle-var-select').forEach(sel => {
      sel.addEventListener('change', (e) => {
        const itemIdx = parseInt(sel.getAttribute('data-item-idx') || '0', 10);
        const chosenId = sel.value;
        selectedVariantIds[itemIdx] = chosenId;
        const opt = sel.options[sel.selectedIndex];
        const newPrice = opt.getAttribute('data-price');
        if (newPrice) {
          const priceLabel = drawer.querySelector(`.vf-bundle-item-price-${itemIdx}`);
          if (priceLabel) priceLabel.textContent = '$' + newPrice;
        }
      });
    });

    drawer.querySelector('.vf-drawer-close').addEventListener('click', () => {
      drawer.classList.remove('open');
    });

    const bundleAtcBtn = drawer.querySelector('.vf-bundle-atc');
    bundleAtcBtn.addEventListener('click', async () => {
      bundleAtcBtn.classList.add('loading');
      bundleAtcBtn.innerHTML = 'Adding Bundle to Bag...';

      const items = selectedVariantIds.map(vId => ({
        id: vId,
        quantity: 1,
        properties: { _vf_vid: vid.id, _vf_wid: widgetId, _vf_bundle: offer?.title || 'Complete The Look' }
      }));

      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
      } catch (e) {}

      trackEvent('inplayer_add_to_cart', {
        shop,
        widgetId,
        videoId: vid.id,
        productId: vid.product?.id,
        eventValue: parseFloat(discountedTotal)
      });

      bundleAtcBtn.classList.remove('loading');
      bundleAtcBtn.classList.add('success');
      bundleAtcBtn.innerHTML = `✓ Entire Look Added! ($${discountedTotal})`;

      showPlayerToast(activeModal, `✓ Complete The Look added to bag! ($${discountedTotal})`);
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

      setTimeout(() => {
        drawer.classList.remove('open');
      }, 1500);
    });
  }

  function showPlayerToast(modal, message) {
    if (!modal) return;
    let toast = modal.querySelector('.videoflow-inplayer-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'videoflow-inplayer-toast';
      modal.querySelector('.videoflow-player-viewport')?.appendChild(toast);
    }
    toast.textContent = message;
    toast.style.display = 'flex';
    clearTimeout(modal._toastTimer);
    modal._toastTimer = setTimeout(() => {
      if (toast) toast.style.display = 'none';
    }, 2200);
  }

  function updateMuteIcon(btn, isMuted) {
    btn.innerHTML = isMuted ? `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
        <line x1="23" y1="9" x2="17" y2="15"></line>
        <line x1="17" y1="9" x2="23" y2="15"></line>
      </svg>
    ` : `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
      </svg>
    `;
  }

  function closePlayer() {
    if (qualifiedTimer) clearTimeout(qualifiedTimer);
    if (activeModal) {
      if (activeModal._dealTimerInterval) clearInterval(activeModal._dealTimerInterval);
      if (activeModal._keyHandler) window.removeEventListener('keydown', activeModal._keyHandler);
      activeModal.remove();
      activeModal = null;
    }
    document.body.style.overflow = '';
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('reel')) {
        url.searchParams.delete('reel');
        window.history.replaceState({}, '', url.toString());
      }
    } catch(e) {}
  }

  // Handle "Shop by Reels" button click anywhere on page
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-action="open-reels"], .reelify-shop-reels-btn');
    if (btn) {
      e.preventDefault();
      let videos = window.__REELIFY_ALL_VIDEOS__;
      const shopDomain = window.Shopify?.shop || '199da1-60.myshopify.com';
      if (videos && videos.length) {
        openFullscreenPlayer(videos, 0, shopDomain, 'shop-by-reels');
      } else {
        fetch(`${API_HOST}/api/storefront/widget?type=carousel&widgetId=home-carousel&shop=${encodeURIComponent(shopDomain)}`)
          .then(r => r.json())
          .then(data => {
            if (data.videos && data.videos.length) {
              window.__REELIFY_ALL_VIDEOS__ = data.videos;
              openFullscreenPlayer(data.videos, 0, shopDomain, 'shop-by-reels');
            }
          })
          .catch(() => {});
      }
    }
  });

  // Deep Link Autoplay support: ?reel=vid-xxx or #reel=vid-xxx
  function checkUrlDeepLink() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const reelId = urlParams.get('reel') || (window.location.hash.startsWith('#reel=') ? window.location.hash.replace('#reel=', '') : null);
      if (reelId) {
        const shopDomain = window.Shopify?.shop || '199da1-60.myshopify.com';
        fetch(`${API_HOST}/api/storefront/widget?type=carousel&widgetId=home-carousel&shop=${encodeURIComponent(shopDomain)}`)
          .then(r => r.json())
          .then(data => {
            if (data.videos && data.videos.length) {
              window.__REELIFY_ALL_VIDEOS__ = data.videos;
              const idx = data.videos.findIndex(v => v.id === reelId);
              openFullscreenPlayer(data.videos, idx !== -1 ? idx : 0, shopDomain, 'deep-link');
            }
          })
          .catch(() => {});
      }
    } catch(e) {}
  }

  function initAllWidgets() {
    const selectors = [
      '.videoflow-widget-container',
      '#videoflow-widget-root',
      '[data-videoflow-widget]',
      '[data-widget-type]',
      '.videoflow-stories-wrapper',
      '.videoflow-carousel-wrapper',
      '.videoflow-pdp-wrapper'
    ];
    const widgets = document.querySelectorAll(selectors.join(', '));
    widgets.forEach(w => {
      if (!w._vf_initialized) {
        w._vf_initialized = true;
        new VideoFlowWidget(w);
      }
    });

    checkUrlDeepLink();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllWidgets);
  } else {
    initAllWidgets();
  }

  // 1-Day ForYou Sale: Live rolling 24hr countdown ticker
  function updateLiveCountdowns() {
    const dayMs = 24 * 60 * 60 * 1000;
    const saleStart = new Date('2026-10-04T00:00:00.000Z').getTime();
    const saleEnd = new Date('2026-10-14T23:59:59.000Z').getTime();
    const now = Date.now();
    if (now > saleEnd) {
      document.querySelectorAll('.vf-timer-val').forEach(el => el.textContent = 'Sale Ended');
      return;
    }
    const elapsed = Math.max(0, (now - saleStart) % dayMs);
    const remainingSec = Math.floor((dayMs - elapsed) / 1000);
    const h = String(Math.floor(remainingSec / 3600)).padStart(2, '0');
    const m = String(Math.floor((remainingSec % 3600) / 60)).padStart(2, '0');
    const s = String(remainingSec % 60).padStart(2, '0');
    const display = `${h}h ${m}m ${s}s`;
    document.querySelectorAll('.vf-timer-val').forEach(el => el.textContent = display);
  }
  setInterval(updateLiveCountdowns, 1000);
})();

